/**
 * Recherche lexicale d'attendus à partir des mots du professeur.
 *
 * Le prof tape « les puissances » ; le référentiel, lui, dit « Utiliser les
 * puissances d'exposants positifs ou négatifs pour simplifier l'écriture des
 * produits ». C'est cette traduction que le panneau Thème puis Descripteur
 * puis Attendu lui fait faire à la main, sur deux cent trente-cinq attendus,
 * à chaque composition. Cette fonction la fait à sa place.
 *
 * Elle tourne ENTIÈREMENT côté client, sur l'arbre que le composeur charge
 * déjà (`themes/?niveau=`). Aucune route, aucun modèle, aucun appel. Le
 * serveur n'est sollicité que lorsqu'elle ne trouve rien : c'est seulement
 * là que le modèle de langue entre en jeu (contrat back du 17/09).
 *
 * Elle ne décide rien : elle classe. Le prof voit les libellés et confirme.
 *
 * Pertinence (2026-09-29, « Coller mes notes » : « Résoudre un problème
 * additif » sortait la bissectrice) : les mots du programme présents partout
 * (résoudre, problème, utiliser, connaître) ne servent plus à chercher ; un
 * mot pèse d'autant plus qu'il est RARE dans le niveau (un mot présent dans
 * 3 attendus pèse plus qu'un mot présent dans 40) ; une petite table de
 * correspondances relie les variantes (additif, addition, somme), un
 * synonyme comptant un peu moins qu'un mot exact.
 */

/** Mots que tout le monde tape et qui ne discriminent rien. */
const MOTS_VIDES = new Set([
    'le', 'la', 'les', 'un', 'une', 'des', 'du', 'de', 'd', 'l', 'au', 'aux',
    'et', 'ou', 'en', 'sur', 'dans', 'avec', 'pour', 'par', 'a', 'ce', 'cette',
    'ces', 'mon', 'ma', 'mes', 'nos', 'notre', 'leur', 'se', 'sa', 'son',
    'je', 'on', 'nous', 'vous', 'il', 'elle',
    'fais', 'fait', 'faire', 'travaille', 'travaillons', 'commence', 'attaque',
    'revois', 'revoir', 'reprends', 'demarre', 'demarrer', 'aborde', 'entame',
    'prepare', 'poursuis', 'continue', 'vois', 'voir', 'chapitre', 'lecon',
    'cours', 'seance',
    'semaine', 'aujourd', 'hui', 'demain', 'lundi', 'mardi', 'mercredi',
    'jeudi', 'vendredi', 'samedi',
]);

/** Minuscules, sans accents, sans ponctuation. */
export const normaliser = (texte) => String(texte || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/**
 * Racine grossière : on coupe le pluriel et les finales de conjugaison les
 * plus courantes. Volontairement pauvre — un vrai lemmatiseur coûterait une
 * dépendance pour gagner des cas que le repli serveur couvre déjà.
 */
const racine = (mot) => mot
    .replace(/(aux)$/, 'al')
    .replace(/[sx]$/, '');

/** Mots significatifs d'une phrase, dédoublonnés, racines comprises. */
export const motsUtiles = (texte) => {
    const vus = new Set();
    normaliser(texte).split(' ').forEach((mot) => {
        if (!mot || mot.length < 2 || MOTS_VIDES.has(mot)) return;
        vus.add(racine(mot));
    });
    return [...vus];
};

/** Un mot est-il présent dans un libellé déjà normalisé et raciné ? */
const contient = (motsDuLibelle, mot) => motsDuLibelle.some(
    (m) => m === mot || (mot.length >= 4 && (m.startsWith(mot) || mot.startsWith(m))),
);

const POIDS = { attendu: 3, descripteur: 2, theme: 1 };

/**
 * Mots du programme présents dans une foule de libellés : ils ne disent rien
 * de l'attendu visé (« Résoudre un problème additif » : seul « additif »
 * compte). Ils ne servent pas à chercher, sauf si la recherche ne contient
 * qu'eux. Racines (sans accent, sans pluriel).
 */
const MOTS_COURANTS_DU_PROGRAMME = new Set(['resoudre', 'probleme', 'utiliser', 'connaitre']);

/**
 * Correspondances « mot du prof ↔ mots du programme » : chaque groupe réunit
 * des variantes qu'un prof emploie l'une pour l'autre. PREMIÈRE LISTE du
 * 2026-09-29, à valider par le prof avant de l'étendre. Racines (après
 * normaliser et racine : sans accent, sans pluriel).
 */
export const CORRESPONDANCES = [
    ['additif', 'addition', 'additionner', 'somme'],
    ['soustractif', 'soustraction', 'soustraire', 'difference'],
    ['multiplicatif', 'multiplication', 'multiplier', 'produit'],
    ['division', 'diviser', 'quotient'],
    ['fraction', 'fractionnaire'],
    ['relatif', 'negatif'],
    ['aire', 'surface'],
];
const VARIANTES = new Map();
CORRESPONDANCES.forEach((groupe) => groupe.forEach((mot) => {
    VARIANTES.set(mot, groupe.filter((autre) => autre !== mot));
}));

/** Un synonyme compte un peu moins qu'un mot exact : l'exact passe devant. */
const POIDS_SYNONYME = 0.75;

/** 1 si le mot est dans le libellé, POIDS_SYNONYME si une de ses variantes y est, 0 sinon. */
const presence = (motsDuLibelle, mot) => {
    if (contient(motsDuLibelle, mot)) return 1;
    return (VARIANTES.get(mot) || []).some((v) => contient(motsDuLibelle, v)) ? POIDS_SYNONYME : 0;
};

/** L'arbre `themes/?niveau=` à plat : un élément par attendu. */
const aplatir = (themes) => themes.flatMap((theme) => (theme?.descripteur || []).flatMap((desc) =>
    (desc?.attendus || [])
        .filter((att) => att?.id)
        .map((att) => ({
            id: att.id,
            description: att.description,
            rubrique: att.rubrique,
            niveau: att.niveau,
            theme: theme?.nom,
            descripteur: desc?.nom,
        }))));

/**
 * Le classement, sur une liste À PLAT d'attendus
 * `{ id, description, rubrique?, theme?, descripteur? }` : chercherAttendus
 * pour l'arbre, la proposition automatique de Coller mes notes pour sa liste.
 * Chaque mot du prof pèse selon sa RARETÉ dans la liste,
 * log(1 + N / nombre d'attendus qui l'ont ou ont une de ses variantes) ;
 * `couverture` est la part de ces poids retrouvée dans le libellé de
 * l'attendu ou de son descripteur (le thème n'y compte pas), entre 0 et 1.
 */
export const classerAttendus = (attendus, texte, options = {}) => {
    const tous = motsUtiles(texte);
    const discriminants = tous.filter((m) => !MOTS_COURANTS_DU_PROGRAMME.has(m));
    const mots = discriminants.length ? discriminants : tous;
    const liste = Array.isArray(attendus) ? attendus.filter((att) => att?.id) : [];
    if (!mots.length || !liste.length) return [];
    const { filtre, max = 8 } = options;

    // Phrase exacte dans le libellé : « multiplication de puissances » vise
    // 1218 et pas 1217, ce qu'un sac de mots ne sait pas distinguer.
    const phrase = normaliser(texte);
    const PRIME_PHRASE = 5;

    const analyses = liste.map((att) => ({
        att,
        motsAtt: motsUtiles(att.description),
        motsDesc: motsUtiles(att.descripteur),
        motsTheme: motsUtiles(att.theme),
    }));
    const N = analyses.length;
    const poids = new Map(mots.map((mot) => {
        const df = analyses.filter(({ motsAtt, motsDesc }) => presence(motsAtt, mot) || presence(motsDesc, mot)).length;
        return [mot, Math.log(1 + N / Math.max(df, 1))];
    }));
    const poidsTotal = mots.reduce((s, mot) => s + poids.get(mot), 0);

    const resultats = [];
    analyses.forEach(({ att, motsAtt, motsDesc, motsTheme }) => {
        let score = (phrase.length >= 4 && normaliser(att.description).includes(phrase)) ? PRIME_PHRASE : 0;
        let couvert = 0;
        mots.forEach((mot) => {
            const p = poids.get(mot);
            const dansAtt = presence(motsAtt, mot);
            if (dansAtt) { score += POIDS.attendu * p * dansAtt; couvert += p * dansAtt; return; }
            const dansDesc = presence(motsDesc, mot);
            if (dansDesc) { score += POIDS.descripteur * p * dansDesc; couvert += p * dansDesc; return; }
            // Le thème est trop large pour expliquer une intention :
            // « calcul » y est dans « Nombres et calculs ». Il départage
            // deux candidats, il n'en qualifie jamais un seul.
            if (presence(motsTheme, mot)) score += POIDS.theme * p;
        });
        if (!couvert) return;

        const enrichi = {
            id: att.id,
            description: att.description,
            rubrique: att.rubrique,
            niveau: att.niveau,
            theme: att.theme,
            descripteur: att.descripteur,
            score,
            couverture: couvert / poidsTotal,
        };
        if (filtre && !filtre(enrichi)) return;
        resultats.push(enrichi);
    });

    // Les mots retrouvés d'abord (pondérés par leur rareté), puis le score,
    // puis l'ordre du référentiel (objectif avant automatisme se fait à
    // l'affichage).
    resultats.sort((a, b) => (b.couverture - a.couverture) || (b.score - a.score) || (a.id - b.id));
    return resultats.slice(0, max);
};

/**
 * Attendus correspondant aux mots du professeur, du plus probable au moins.
 *
 * @param {Array} themes arbre brut de `themes/?niveau=`
 * @param {string} texte ce que le prof a tapé
 * @param {{filtre?: Function, max?: number}} options `filtre` reçoit l'attendu
 *   enrichi et décide s'il est proposable (ouverture, présence d'exerciseur) ;
 *   au caller de le fournir, la fonction ne sait rien de l'ouverture.
 * @returns {Array<{id, description, rubrique, theme, descripteur, score, couverture}>}
 *   `couverture` : cf. classerAttendus. Liste VIDE quand rien ne correspond :
 *   c'est le signal d'appeler le serveur.
 */
export const chercherAttendus = (themes, texte, options = {}) => (
    Array.isArray(themes) ? classerAttendus(aplatir(themes), texte, options) : []
);

/**
 * En dessous, on n'affiche rien et on laisse la main au serveur : une moitié
 * des mots retrouvés sur un seul libellé, c'est du hasard, pas une réponse.
 */
export const SEUIL_CONFIANCE = 0.6;

/** Résultats assez sûrs pour être montrés au prof sans passer par le serveur. */
export const resultatsFiables = (resultats) => resultats.filter((r) => r.couverture >= SEUIL_CONFIANCE);
